import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { getCategories } from '@/api/labels';
import {
  useAssignLabelToCategory,
  useAssignLabelToPost,
  useCreateCategory,
  useCreateLabels,
  useDeleteLabelToPost
} from '@/hooks/queries/labels';
import useAlert from '@/hooks/useAlert';
import { Labels } from '@/models/labels';

import { PendingLabel } from '../types';
import { isPresent, isSameName } from '../utils/matching';

type SaveArgs = {
  postId: number;
  labels: Labels.Details[];
  pending: PendingLabel[];
  removing: number[];
  setPending: (item: PendingLabel[]) => void;
  setRemoving: (ids: number[]) => void;
};

export const useSaveLabel = () => {
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();
  const alert = useAlert();

  const { mutateAsync: createLabel } = useCreateLabels();
  const { mutateAsync: assignLabelToPost } = useAssignLabelToPost();
  const { mutateAsync: deleteLabelToPost } = useDeleteLabelToPost();
  const { mutateAsync: createCategory } = useCreateCategory();
  const { mutateAsync: assignLabelToCategory } = useAssignLabelToCategory();

  const saveItem = async (
    item: PendingLabel,
    postId: number,
    resolveCategoryId: (name: string) => Promise<number>
  ): Promise<PendingLabel | null> => {
    let current = item;
    try {
      const categoryNew =
        item.categoryId === undefined && item.categoryName !== undefined;
      const categoryId =
        item.categoryId ??
        (item.categoryName
          ? await resolveCategoryId(item.categoryName)
          : undefined);

      const labelCreated = item.id === undefined;
      const id = labelCreated
        ? (await createLabel({ name: item.name })).data.id
        : item.id;
      current = { ...current, id };

      if (
        categoryId !== undefined &&
        (categoryNew || labelCreated || item.needsLink)
      ) {
        await assignLabelToCategory({ categoryId, labelId: id as number });
      }
      await assignLabelToPost({
        id: id as number,
        postId,
        payload: { categoryId }
      });

      return null;
    } catch {
      return current;
    }
  };

  const removeItem = async (
    label: Labels.Details,
    postId: number
  ): Promise<number | null> => {
    try {
      await deleteLabelToPost({
        id: label.id,
        postId,
        payload: { categoryId: label.category?.id }
      });
      return null;
    } catch {
      return label.id;
    }
  };

  const handleSave = async ({
    postId,
    labels,
    pending,
    removing,
    setPending,
    setRemoving
  }: SaveArgs) => {
    setIsSaving(true);
    const categoryId = new Map<string, Promise<number>>();

    const resolveCategoryId = (categoryName: string): Promise<number> => {
      const key = categoryName.toLocaleLowerCase();
      const cache = categoryId.get(key);
      if (cache) return cache;

      const promise = createCategory({ name: categoryName })
        .then((r) => r.data.id)
        .catch(async (e) => {
          const errorId = e?.responde?.data?.id;
          if (errorId !== undefined) return errorId;
          const { data } = await getCategories({});
          const found = data.results.find((c) =>
            isSameName(c.name, categoryName)
          );
          if (found) return found.id;
          throw e;
        });

      categoryId.set(key, promise);
      return promise;
    };

    const labelsToRemove = labels.filter((l) => removing.includes(l.id));
    const [addResults, removeResults] = await Promise.all([
      Promise.all(pending.map((i) => saveItem(i, postId, resolveCategoryId))),
      Promise.all(labelsToRemove.map((l) => removeItem(l, postId)))
    ]);

    const failedAdds = addResults.filter(isPresent);
    const failedRemovals = removeResults.filter(isPresent);

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['labels'] }),
      queryClient.invalidateQueries({ queryKey: ['labelsCategories'] }),
      queryClient.invalidateQueries({ queryKey: ['labelsCategory'] }),
      queryClient.invalidateQueries({ queryKey: ['category'] }),
      queryClient.invalidateQueries({ queryKey: ['violation'] }),
      queryClient.invalidateQueries({
        queryKey: ['post', postId],
        refetchType: 'all'
      }),
      queryClient.invalidateQueries({ queryKey: ['creatorsPosts'] })
    ]);

    setPending(failedAdds);
    setRemoving(failedRemovals);
    setIsSaving(false);

    const failedCount = failedAdds.length + failedRemovals.length;
    if (failedCount > 0)
      return alert.error(
        `${failedCount} change could not be saved. Try again.`
      );
    return alert.success('Labels changed saved.');
  };

  return { isSaving, handleSave };
};
